import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import type {
  AsignarAlcanceRequest,
  CuentaAsignable,
  ListaAlcances,
  TipoAlcance,
} from '../types/metas.types';

interface Props {
  opciones: ListaAlcances['opciones'];
  /** Cuentas elegibles (rol Indicadores). Si se da `userIdFijo`, no se muestra el selector de cuenta. */
  cuentas?: CuentaAsignable[];
  userIdFijo?: string;
  ocupado?: boolean;
  onAsignar: (req: AsignarAlcanceRequest) => void | Promise<void>;
  /**
   * Clase de las listas desplegables (que PrimeReact pinta fuera, en `body`).
   * La pestaña «Accesos» pasa la del módulo; en Usuarios se omite, para que la
   * pantalla de Usuarios no herede la fuente ni los tokens de metas.
   */
  claseOverlay?: string;
}

const TIPOS: { label: string; value: TipoAlcance }[] = [
  { label: 'Supervisor', value: 'supervisor' },
  { label: 'Regional', value: 'regional' },
  { label: 'Dirección', value: 'direccion' },
];

/** Lo que gana una cuenta con el alcance de dirección, dicho donde se asigna. */
export const TEXTO_DIRECCION =
  'Ve todas las tiendas en sólo lectura: el seguimiento de la semana (quién envió y quién autorizó), Alcances y Pendientes de catálogo. No captura, no autoriza ni reabre, y no ve Accesos.';

/**
 * Asignar «supervisor de <NOMBRE>» o «regional de <NOMBRE>» a una cuenta, o el
 * de dirección (21-oct-2026, decisión del usuario), que no lleva nombre: ve
 * todas las tiendas y la API lo guarda con un nombre fijo.
 *
 * ⚠️ Los nombres salen SÓLO de las opciones que manda la API —las del catálogo
 * de tiendas, con su grafía exacta— y nunca de un campo libre: el nombre es la
 * LLAVE de acceso, y «Esther Morales» escrito a mano no vería ninguna tienda.
 */
export const AsignarAlcanceForm: React.FC<Props> = ({
  opciones,
  cuentas,
  userIdFijo,
  ocupado,
  onAsignar,
  claseOverlay,
}) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [tipo, setTipo] = useState<TipoAlcance>('supervisor');
  const [nombre, setNombre] = useState<string | null>(null);

  const opcionesCuenta = useMemo(
    () =>
      (cuentas ?? []).map((c) => ({
        label: c.name ? `${c.email} · ${c.name}` : c.email,
        value: c.id,
      })),
    [cuentas],
  );
  const opcionesNombre = useMemo(
    () =>
      (tipo === 'supervisor' ? opciones.supervisores : opciones.regionales).map((o) => ({
        label: `${o.nombre} (${o.tiendas} tienda${o.tiendas === 1 ? '' : 's'})`,
        value: o.nombre,
      })),
    [opciones, tipo],
  );

  const cuenta = userIdFijo ?? userId;
  const direccion = tipo === 'direccion';
  const listo = !!cuenta && (direccion || !!nombre) && !ocupado;

  const asignar = async () => {
    if (!cuenta) return;
    if (direccion) {
      await onAsignar({ userId: cuenta, tipo });
      return;
    }
    if (!nombre) return;
    await onAsignar({ userId: cuenta, tipo, nombre });
    setNombre(null);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="asignar-alcance">
      {!userIdFijo && (
        <div className="flex flex-col gap-1">
          <label htmlFor="alcance-cuenta" className="font-semibold">
            Cuenta (rol Indicadores)
          </label>
          <Dropdown
            inputId="alcance-cuenta"
            value={userId}
            options={opcionesCuenta}
            onChange={(e) => setUserId(e.value ?? null)}
            filter
            placeholder="Elige la cuenta"
            emptyMessage="No hay cuentas con rol Indicadores"
            emptyFilterMessage="Ninguna cuenta coincide"
            className="w-full"
            panelClassName={claseOverlay}
          />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <span className="font-semibold">Tipo</span>
        <SelectButton
          value={tipo}
          options={TIPOS}
          onChange={(e) => {
            if (!e.value) return;
            setTipo(e.value);
            setNombre(null);
          }}
          allowEmpty={false}
        />
      </div>
      {direccion ? (
        // Sin nombre del catálogo: dirección no es la llave de ninguna tienda.
        <p className="m-0 text-sm text-gray-600" data-testid="alcance-direccion-nota">
          {TEXTO_DIRECCION}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          <label htmlFor="alcance-nombre" className="font-semibold">
            {tipo === 'supervisor' ? 'Supervisor' : 'Regional'} del catálogo
          </label>
          <Dropdown
            inputId="alcance-nombre"
            value={nombre}
            options={opcionesNombre}
            onChange={(e) => setNombre(e.value ?? null)}
            filter
            placeholder="Elige el nombre"
            className="w-full"
            panelClassName={claseOverlay}
          />
        </div>
      )}
      <div>
        <Button
          label="Asignar"
          icon="pi pi-user-plus"
          onClick={() => void asignar()}
          disabled={!listo}
          loading={ocupado}
        />
      </div>
    </div>
  );
};

export default AsignarAlcanceForm;
